import { useState } from 'react';

import type { FundPolicy } from '../../core/fundPolicy/schema';
import { RELATION_LABELS_AR } from '../../features/employees/application/dependentLabels';
import {
  applySettings,
  fromPolicy,
  type SettingsFormValues,
} from '../../features/settings/application/settingsForm';
import { SettingsPage } from '../../pages/SettingsPage';

import { useServices } from '../providers/servicesContext';

/**
 * Connects the settings screen to its port.
 *
 * The saved policy is not re-read afterwards. The employee screens read it on
 * their next mount, and telling the manager it saved when the write succeeded
 * is the honest report — a stale local copy claiming otherwise is worse.
 */
export function SettingsScreen({
  policy,
  onSaved,
}: {
  readonly policy: FundPolicy;
  readonly onSaved: () => void;
}) {
  const { settings } = useServices();
  const [errorAr, setErrorAr] = useState<string | null>(null);
  const [savedAr, setSavedAr] = useState<string | null>(null);

  async function handleSave(values: SettingsFormValues): Promise<void> {
    setErrorAr(null);
    setSavedAr(null);

    try {
      await settings.saveFundPolicy(applySettings(policy, values));
      setSavedAr('حُفظت الإعدادات. تسري على الموظفين فورًا.');
      onSaved();
    } catch (error) {
      setErrorAr(error instanceof Error ? error.message : 'تعذّر حفظ الإعدادات.');
    }
  }

  return (
    <SettingsPage
      relations={policy.dependentRules.map((rule) => ({
        relation: rule.relation,
        labelAr: RELATION_LABELS_AR[rule.relation],
      }))}
      initialValues={fromPolicy(policy)}
      onSave={handleSave}
      errorAr={errorAr}
      savedAr={savedAr}
    />
  );
}
